<template>
  <a-modal
    v-model:visible="showAddField"
    centered
    closable
    title="Add Config Field"
    :footer="null"
    destroyOnClose
  >
    <add-config-field @created="onFieldCreated"></add-config-field>
  </a-modal>

  <a-modal
    v-model:visible="showDeleteFields"
    centered
    closable
    title="Delete Config Fields"
    :footer="null"
    destroyOnClose
    width="700px"
  >
    <a-table
      :columns="deleteFieldColumns"
      :data-source="globalFieldsList"
      bordered
      :pagination="false"
      :scroll="{ y: '400px' }"
      row-key="key"
      size="small"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'default_value'">
          {{ formatValue(record.default_value) }}
        </template>
        <template v-if="column.dataIndex === 'actions'">
          <a-popconfirm
            title="Delete this config field globally?"
            ok-text="Yes"
            cancel-text="No"
            @confirm="deleteFieldFromModal(record.key)"
          >
            <a-button type="link" danger size="small">
              <i class="bi bi-trash"></i> Delete
            </a-button>
          </a-popconfirm>
        </template>
      </template>
    </a-table>
  </a-modal>

  <a-row :gutter="[8, 8]" class="py-1 mb-2 px-1">
    <a-col span="6">
      <a-select
        v-model:value="selectedOrg"
        style="width: 100%"
        placeholder="Select Organization"
        :options="organizationOptions"
        :filter-option="filterOption"
        show-search
        @change="onOrgChange"
      ></a-select>
    </a-col>
    <a-col span="6">
      <a-select
        v-model:value="selectedDevice"
        style="width: 100%"
        placeholder="Select Device"
        :options="deviceOptions"
        :filter-option="filterOption"
        show-search
        :disabled="!selectedOrg"
        @change="onDeviceChange"
      ></a-select>
    </a-col>
    <a-col span="12">
      <a-button
        class="float-right d-flex align-items-center"
        type="primary"
        @click="showAddField = true"
      >
        Add Config Field
      </a-button>
      <a-button
        class="float-right d-flex align-items-center mr-2"
        type="primary"
        danger
        @click="openDeleteModal"
      >
        Delete Config Field
      </a-button>
    </a-col>

    <a-col v-if="selectedDevice" span="24" class="mt-4">
      <a-table
        :columns="columns"
        :data-source="configList"
        bordered
        :pagination="false"
        :scroll="{ y: '55vh' }"
        row-key="key"
      >
        <template #title>
          <a-typography-title :level="5">
            Device Config: &nbsp;
            <a-tag color="blue">{{ configList.length }}</a-tag>
          </a-typography-title>
        </template>
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'value'">
            <template v-if="editingKey === record.key">
              <a-select
                v-if="record.value_type === 'bool'"
                v-model:value="editValue"
                :options="boolOptions"
                style="width: 100%"
                size="small"
              ></a-select>
              <a-input-number
                v-else-if="record.value_type === 'int' || record.value_type === 'float'"
                v-model:value="editValue"
                size="small"
                style="width: 100%"
                :precision="record.value_type === 'int' ? 0 : undefined"
              ></a-input-number>
              <a-textarea
                v-else-if="record.value_type === 'json'"
                v-model:value="editValue"
                size="small"
                :rows="2"
                style="width: 100%"
              ></a-textarea>
              <a-input
                v-else
                v-model:value="editValue"
                size="small"
                style="width: 100%"
              ></a-input>
            </template>
            <span v-else>{{ formatValue(record.value) }}</span>
          </template>
          <template v-if="column.dataIndex === 'actions'">
            <template v-if="editingKey === record.key">
              <a-button type="link" size="small" @click="saveEdit(record)">Save</a-button>
              <a-button type="link" size="small" danger @click="cancelEdit">Cancel</a-button>
            </template>
            <template v-else>
              <a-button type="link" size="small" @click="startEdit(record)">
                Edit
              </a-button>
              <a-popconfirm
                v-if="!record.is_default"
                title="Reset this field to global default?"
                ok-text="Yes"
                cancel-text="No"
                @confirm="resetField(record.key)"
              >
                <a-button type="link" size="small">Reset</a-button>
              </a-popconfirm>
            </template>
          </template>
        </template>
      </a-table>
    </a-col>
  </a-row>
</template>

<script>
import DeviceServices from '../../../../services/device';
import UserConfigService from '../../../../services/userConfig';
import AddConfigField from './AddConfigField.vue';

export default {
  components: { AddConfigField },
  inject: ['toast'],
  data() {
    return {
      showAddField: false,
      showDeleteFields: false,
      globalFieldsList: [],
      selectedOrg: null,
      selectedDevice: null,
      organizationList: [],
      deviceList: [],
      configList: [],
      editingKey: null,
      editValue: null,
      boolOptions: [
        { value: true, label: 'true' },
        { value: false, label: 'false' },
      ],
    };
  },
  computed: {
    organizationOptions() {
      return this.organizationList.map((org) => ({
        value: org.org_name_to_display,
        label: org.org_name_to_display,
      }));
    },
    deviceOptions() {
      return this.deviceList.map((device) => ({
        value: device.Device,
        label: device.display_name
          ? `${device.display_name} (${device.Serial_number})`
          : device.Serial_number,
      }));
    },
    deleteFieldColumns() {
      return [
        { title: 'Key', dataIndex: 'key', width: '30%' },
        { title: 'Default Value', dataIndex: 'default_value', width: '25%' },
        { title: 'Type', dataIndex: 'value_type', width: '15%' },
        { title: 'Actions', dataIndex: 'actions', width: '15%', align: 'center' },
      ];
    },
    columns() {
      return [
        { title: 'Key', dataIndex: 'key', width: '18%' },
        { title: 'Value', dataIndex: 'value', width: '22%' },
        { title: 'Description', dataIndex: 'description', width: '32%' },
        { title: 'Type', dataIndex: 'value_type', width: '10%' },
        { title: 'Actions', dataIndex: 'actions', width: '18%', align: 'center' },
      ];
    },
  },
  created() {
    this.getOrganizations();
  },
  methods: {
    filterOption(input, option) {
      return option.label.toLowerCase().includes(input.toLowerCase());
    },
    formatValue(val) {
      if (val === null || val === undefined) return '-';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    },
    async getOrganizations() {
      const [error, data] = await DeviceServices.getAllOrganizations();
      if (error) return;
      this.organizationList = data;
    },
    async onOrgChange() {
      this.selectedDevice = null;
      this.deviceList = [];
      this.configList = [];
      if (!this.selectedOrg) return;
      const [error, data] = await DeviceServices.fetchAllDevicesOfOrg(
        this.selectedOrg
      );
      if (error) return;
      this.deviceList = data;
    },
    async onDeviceChange() {
      if (!this.selectedDevice) {
        this.configList = [];
        return;
      }
      this.cancelEdit();
      await this.fetchDeviceConfig();
    },
    async fetchDeviceConfig() {
      const [fieldsErr, fieldsData] = await UserConfigService.getConfigFields();
      const descriptionMap = {};
      if (!fieldsErr && fieldsData) {
        fieldsData.forEach((f) => { descriptionMap[f.key] = f.description || ''; });
      }

      const [error, data] = await UserConfigService.getDeviceConfig(
        this.selectedDevice
      );
      if (error) {
        this.configList = [];
        return;
      }
      this.configList = Object.entries(data).map(([key, info]) => ({
        key,
        value: info.value,
        value_type: info.value_type,
        is_default: info.is_default,
        description: descriptionMap[key] || '-',
      }));
    },
    startEdit(record) {
      this.editingKey = record.key;
      this.editValue =
        record.value_type === 'json' && typeof record.value === 'object'
          ? JSON.stringify(record.value)
          : record.value;
    },
    cancelEdit() {
      this.editingKey = null;
      this.editValue = null;
    },
    async saveEdit(record) {
      let val = this.editValue;
      if (record.value_type === 'json' && typeof val === 'string') {
        try {
          val = JSON.parse(val);
        } catch {
          this.toast.error('Invalid JSON value');
          return;
        }
      }
      const payload = { [record.key]: val };
      const [error] = await UserConfigService.updateDeviceConfig(
        this.selectedDevice,
        payload
      );
      if (error) {
        const errData = error?.response?.data;
        const msg =
          errData?.errors?.join(', ') ||
          errData?.error ||
          'Failed to update config';
        this.toast.error(msg);
        return;
      }
      this.toast.success('Config updated');
      this.cancelEdit();
      await this.fetchDeviceConfig();
    },
    async resetField(key) {
      const [error] = await UserConfigService.resetDeviceConfig(
        this.selectedDevice,
        [key]
      );
      if (error) {
        this.toast.error('Failed to reset config');
        return;
      }
      this.toast.success('Config reset to default');
      await this.fetchDeviceConfig();
    },
    async openDeleteModal() {
      this.showDeleteFields = true;
      await this.fetchDeleteModalFields();
    },
    async fetchDeleteModalFields() {
      const [error, data] = await UserConfigService.getConfigFields();
      if (error) {
        this.globalFieldsList = [];
        return;
      }
      this.globalFieldsList = data.map((f) => ({
        key: f.key,
        default_value: f.default_value,
        value_type: f.value_type,
      }));
    },
    async deleteFieldFromModal(key) {
      const [error] = await UserConfigService.deleteConfigField(key);
      if (error) {
        this.toast.error('Failed to delete config field');
        return;
      }
      this.toast.success('Config field deleted');
      await this.fetchDeleteModalFields();
      if (this.selectedDevice) {
        await this.fetchDeviceConfig();
      }
    },
    async onFieldCreated() {
      this.showAddField = false;
      if (this.selectedDevice) {
        await this.fetchDeviceConfig();
      }
    },
  },
};
</script>
